import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

// 페이지 설정 ( path variable 변수 제거, 페이지 타이틀 )
const PAGE_CONFIG = [
  {
    key: '/mtg/detail/',
    regex: /\/mtg\/detail\/[^/]+\/[^/]+/,
    cleanPath: '/mtg/detail',
    title: '주담대 상품 상세',
  },
  {
    key: '/detail/',
    regex: /\/detail\/[^/]+\/[^/]+/,
    cleanPath: '/detail',
    title: '신용대출 상품 상세',
  },
  {
    key: '/preview/promotion/',
    regex: /\/preview\/promotion\/[^/]+/,
    cleanPath: '/preview/promotion',
    title: '프로모션 미리보기',
  },
  {
    key: '/promotion/',
    regex: /\/promotion\/[^/]+/,
    cleanPath: '/promotion',
    title: '프로모션',
  },
  {
    key: '/cardplate/',
    regex: /\/cardplate\/[^/]+/,
    cleanPath: '/cardplate',
    title: '카드 플레이트',
  },
  {
    key: '/survey/',
    regex: /\/survey\/[^/]+\/[^/]+/,
    cleanPath: '/survey',
    title: '설문조사',
  },

  { key: '/extlink', cleanPath: '/extlink', title: '외부 경로 유입' },
  { key: '/main', cleanPath: '/main', title: '메인 홈' },
  { key: '/partners', cleanPath: '/partners', title: '제휴사 목록' },
  { key: '/mtg/loading', cleanPath: '/mtg/loading', title: '주담대 로딩' },
  { key: '/loading', cleanPath: '/loading', title: '신용 조회 로딩' },
  { key: '/mtg/result', cleanPath: '/mtg/result', title: '주담대 결과 화면' },
  { key: '/result', cleanPath: '/result', title: '결과 화면' },
  { key: '/term', cleanPath: '/term', title: '약관 화면' },
  { key: '/mtg/rateCalc', cleanPath: '/mtg/rateCalc', title: '주담대 이자계산기' },
  { key: '/rateCalc', cleanPath: '/rateCalc', title: '신용 이자계산기' },
  { key: '/calltime', cleanPath: '/calltime', title: '주담대 전화 안내' },
  { key: '/mtg/result-complete', cleanPath: '/mtg/result-complete', title: '주담대 신청 완료' },
  { key: '/mtg/result-status', cleanPath: '/mtg/result-status', title: '주담대 신청 상태보기' },
  { key: '/mtg/conditions/1', cleanPath: '/mtg/conditions/1', title: '주담대 대출목적 입력' },
  { key: '/mtg/conditions/2', cleanPath: '/mtg/conditions/2', title: '주담대 보유주택 입력' },
  { key: '/mtg/conditions/3', cleanPath: '/mtg/conditions/3', title: '주담대 담보주택 주소 입력' },
  { key: '/mtg/conditions/4', cleanPath: '/mtg/conditions/4', title: '주담대 담보주택 정보 입력' },
  { key: '/mtg/conditions/5', cleanPath: '/mtg/conditions/5', title: '주담대 결혼소득 입력' },
  { key: '/mtg/conditions/6', cleanPath: '/mtg/conditions/6', title: '주담대 신용점수 입력' },
  { key: '/mtg/conditions/7', cleanPath: '/mtg/conditions/7', title: '주담대 입력 정보확인' },
  { key: '/conditions/1', cleanPath: '/conditions/1', title: '신용 주민번호 입력' },
  { key: '/conditions/1/confirm', cleanPath: '/conditions/1/confirm', title: '신용 인증번호 입력' },
  { key: '/conditions/3', cleanPath: '/conditions/3', title: '신용 직업소득 입력' },
  { key: '/conditions/5', cleanPath: '/conditions/5', title: '신용 상세정보 입력' },
];

const AnalyticsTracker = () => {
  const location = useLocation();
  const { media } = useParams();

  // 현재 주소에 맞는 설정 찾기
  const getPageInfo = (pathname: string) => {
    const matched = PAGE_CONFIG.find((p) => {
      if (p.regex) {
        return pathname.includes(p.key);
      }
      return pathname.endsWith(p.key);
    });

    if (matched) {
      return {
        path: matched.regex
          ? pathname.replace(matched.regex, matched.cleanPath)
          : matched.cleanPath,
        title: matched.title,
      };
    }

    // 없으면 기본값
    return {
      path: pathname,
      title: document.title,
    };
  };

  // 통신사
  const pathCarrier = location.pathname.split('/')[1];
  const carrier =
    media ||
    (pathCarrier === 'skt' || pathCarrier === 'kt' || pathCarrier === 'lgu'
      ? pathCarrier
      : undefined);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function')
      return;
    if (!carrier) return;

    window.gtag('set', 'user_properties', { carrier });
    window.gtag('set', { carrier });
  }, [carrier]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function')
      return;

    // 루트(/)나 통신사 메인(/skt) 제외
    if (location.pathname === '/') return;
    if (['/skt', '/kt', '/lgu'].includes(location.pathname)) return;

    // 정보 가져오기
    const { path: cleanPath, title: originTitle } = getPageInfo(location.pathname);
    const pageTitle = carrier ? `[${carrier}] ${originTitle}` : originTitle;
    const cleanLocation = window.location.origin + cleanPath + location.search;

    window.gtag("set", "page_location", cleanLocation);
    window.gtag("set", "page_path", cleanPath);

    // GA 전송
    window.gtag('event', 'page_view', {
      page_title: pageTitle,
      page_location: cleanLocation,
      page_path: cleanPath,
      carrier,
    });
  }, [location.pathname, location.search, carrier]);

  return null;
};

export default AnalyticsTracker;